import { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { API_BASE } from '../../lib/api-base';
import { Button } from '../../components/ui/button';
import {
  Database, HardDrive, Trash2, Play, Download, RefreshCw, AlertCircle, CheckCircle2,
} from 'lucide-react';

function fmtBytes(b) {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = b;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${u[i]}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

export default function MaintenanceTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/maintenance/status');
    if (r.ok) setStatus(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const trigger = async (label, path, method = 'POST') => {
    setBusy(label);
    try {
      const r = await authFetch(path, { method });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { flash('error', d.detail || 'Aktion fehlgeschlagen'); return; }
      // Spezifisches Feedback je Aktion
      if (d.deleted !== undefined) flash('success', `${d.deleted} Anhänge gelöscht`);
      else if (d.updated !== undefined) flash('success', `${d.updated} Rechnungen bereinigt`);
      else if (d.ok && d.snapshot) flash('success', `Backup erstellt: ${d.snapshot.split('/').pop()}`);
      else if (d.deleted) flash('success', `Gelöscht: ${d.deleted}`);
      else flash('success', 'Erledigt');
      await load();
    } finally {
      setBusy(null);
    }
  };

  const deleteBackup = async (name) => {
    if (!window.confirm(`Backup "${name}" wirklich löschen?`)) return;
    setBusy(name);
    const r = await authFetch(`/api/maintenance/backup/${name}`, { method: 'DELETE' });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { flash('success', `Backup "${name}" gelöscht`); await load(); }
    else flash('error', d.detail || 'Fehler');
    setBusy(null);
  };

  const downloadBackup = (name) => {
    const url = `${API_BASE}/api/maintenance/backup/${name}.tar.gz`;
    // Token im Header → eigene fetch-Logik, dann blob
    authFetch(`/api/maintenance/backup/${name}.tar.gz`).then(async r => {
      if (!r.ok) { flash('error', 'Download fehlgeschlagen'); return; }
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.tar.gz`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1500);
    });
    // url ist nur für Browser-Direktnavigation
    void url;
  };

  if (loading) return <div className="text-sm text-gray-400 p-4">Lade…</div>;
  if (!status) return <div className="text-sm text-red-500 p-4">Status konnte nicht geladen werden.</div>;

  const collections = Object.entries(status.collections || {});

  return (
    <div data-testid="maintenance-tab" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-[#32373c] flex items-center gap-2">
            <Database className="w-4 h-4 text-[#9ec043]" /> Wartung & Backups
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            DB-Größen, manuelle Cleanups und tägliche Backups verwalten.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="text-xs h-8" data-testid="btn-maint-reload">
          <RefreshCw className="w-3 h-3 mr-1" /> Aktualisieren
        </Button>
      </div>

      {msg && (
        <div
          className={`flex items-center gap-2 text-sm rounded p-3 border ${
            msg.type === 'success'
              ? 'text-green-700 bg-green-50 border-green-200'
              : 'text-red-700 bg-red-50 border-red-200'
          }`}
          data-testid="maint-msg"
        >
          {msg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {msg.text}
        </div>
      )}

      {/* Collection-Größen */}
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Datenbank-Größen</h3>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs text-gray-500">
                <th className="px-4 py-2 font-medium">Collection</th>
                <th className="px-4 py-2 font-medium text-right">Datensätze</th>
                <th className="px-4 py-2 font-medium text-right">Speicher</th>
              </tr>
            </thead>
            <tbody>
              {collections.map(([name, s]) => (
                <tr key={name} className="border-t border-gray-100" data-testid={`coll-row-${name}`}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">{name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{s.count.toLocaleString('de-DE')}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    <span className={s.sizeMb > 50 ? 'text-orange-600 font-medium' : ''}>
                      {fmtBytes(s.sizeBytes)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Cleanup-Aktionen */}
      <section>
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Cleanup</h3>
        <p className="text-xs text-gray-400 mb-3">
          Automatisch stündlich. Manueller Trigger für Sofort-Bereinigung.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <button
            onClick={() => trigger('old', '/api/maintenance/cleanup/attachments-old')}
            disabled={busy === 'old'}
            className="bg-white border border-gray-200 hover:border-[#9ec043] hover:shadow-sm rounded-lg p-4 text-left disabled:opacity-50 transition"
            data-testid="btn-cleanup-old"
          >
            <div className="text-sm font-medium text-[#32373c]">Alte Anhänge</div>
            <p className="text-xs text-gray-400 mt-1">
              Anhänge älter als {status.config.attachmentRetentionDays} Tage löschen.
            </p>
            <div className="text-xs text-[#9ec043] mt-2 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> {busy === 'old' ? 'läuft…' : 'Jetzt ausführen'}
            </div>
          </button>

          <button
            onClick={() => trigger('closed', '/api/maintenance/cleanup/attachments-closed')}
            disabled={busy === 'closed'}
            className="bg-white border border-gray-200 hover:border-[#9ec043] hover:shadow-sm rounded-lg p-4 text-left disabled:opacity-50 transition"
            data-testid="btn-cleanup-closed"
          >
            <div className="text-sm font-medium text-[#32373c]">Erledigt-Anhänge</div>
            <p className="text-xs text-gray-400 mt-1">
              Anhänge zu allen Anfragen mit Status „erledigt" entfernen.
            </p>
            <div className="text-xs text-[#9ec043] mt-2 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> {busy === 'closed' ? 'läuft…' : 'Jetzt ausführen'}
            </div>
          </button>

          <button
            onClick={() => trigger('invoices', '/api/maintenance/cleanup/invoice-pdfs')}
            disabled={busy === 'invoices'}
            className="bg-white border border-gray-200 hover:border-[#9ec043] hover:shadow-sm rounded-lg p-4 text-left disabled:opacity-50 transition"
            data-testid="btn-cleanup-invoices"
          >
            <div className="text-sm font-medium text-[#32373c]">Bestätigte Rechnungen</div>
            <p className="text-xs text-gray-400 mt-1">
              PDF-Blobs bestätigter Lager-Rechnungen entfernen ({status.pendingInvoicePdfs} mit PDF).
            </p>
            <div className="text-xs text-[#9ec043] mt-2 inline-flex items-center gap-1">
              <Trash2 className="w-3 h-3" /> {busy === 'invoices' ? 'läuft…' : 'Jetzt ausführen'}
            </div>
          </button>
        </div>
      </section>

      {/* Backups */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Backups</h3>
            <p className="text-xs text-gray-400">
              Täglich automatisch nach <span className="font-mono">{status.config.backupDir}</span>,
              Rolling-Aufbewahrung {status.config.backupRetentionDays} Tage.
            </p>
          </div>
          <Button
            onClick={() => trigger('backup', '/api/maintenance/backup/run')}
            disabled={busy === 'backup'}
            className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm"
            data-testid="btn-backup-run"
          >
            <Play className="w-3.5 h-3.5 mr-1" /> {busy === 'backup' ? 'Erstelle…' : 'Backup jetzt erstellen'}
          </Button>
        </div>

        {!status.backups?.length ? (
          <div className="bg-white rounded-lg border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
            <HardDrive className="w-6 h-6 mx-auto mb-2 text-gray-300" />
            Noch keine Backups vorhanden.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-500">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Erstellt</th>
                  <th className="px-4 py-2 font-medium text-right">Größe</th>
                  <th className="px-4 py-2 font-medium text-right">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {status.backups.map(b => (
                  <tr key={b.name} className="border-t border-gray-100" data-testid={`backup-row-${b.name}`}>
                    <td className="px-4 py-2 font-mono text-xs text-gray-700">{b.name}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">{fmtDate(b.createdAt)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtBytes(b.sizeBytes)}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => downloadBackup(b.name)}
                        className="p-1.5 text-gray-400 hover:text-[#9ec043] rounded inline-flex"
                        title="Als .tar.gz herunterladen"
                        data-testid={`btn-backup-dl-${b.name}`}
                      >
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => deleteBackup(b.name)}
                        disabled={busy === b.name}
                        className="p-1.5 text-gray-400 hover:text-red-500 rounded inline-flex ml-1 disabled:opacity-50"
                        title="Löschen"
                        data-testid={`btn-backup-del-${b.name}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
